
import { validationMixin } from 'vuelidate';
import { defineComponent } from '@nuxtjs/composition-api';
import type { PropOptions } from 'vue';
import type { AutocompleteCompany } from '@/types/api';
import MultiselectForm from '@/components/common/MultiselectForm/MultiselectForm.vue';
import type {
  Company,
  Client,
  CompaniesSearchQuery as CompaniesSearchQueryType,
  CompaniesSearchQueryVariables,
} from '@/types/graphql';
import PortfolioClientMixin from '@/components/partials/portfolio/PortfolioClientMixin/PortfolioClientMixin';
import CompaniesSearchQuery from '@/apollo/queries/CompaniesSearch.query';

const MIN_CHARS = 2;

export default defineComponent({
  components: {
    MultiselectForm,
  },
  mixins: [validationMixin, PortfolioClientMixin],
  props: {
    label: {
      type: String,
      default: '',
    },
    clients: {
      type: Array,
      required: true,
      default: () => [],
    } as PropOptions<Client[]>,
    dropdownPosition: {
      type: String,
      default: 'down',
      validator: (value: string) => ['up', 'down'].includes(value),
    },
    fullCompanyName: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    attributeName: {
      type: String,
      required: true,
    },
  },
  apollo: {
    companiesSearch: {
      query: CompaniesSearchQuery,
      variables(): CompaniesSearchQueryVariables {
        return {
          query: this.query,
        };
      },
      update: ({ companiesSearch }: CompaniesSearchQueryType) =>
        companiesSearch.map(company => ({
          id: company.id,
          logo: company.logoUrl,
          name: company.name,
          url: company.webSiteUrl,
        })),
      skip() {
        return !this.query || !this.query.trim() || this.query.length < MIN_CHARS;
      },
      debounce: 250,
      fetchPolicy: 'network-only',
    },
  },
  data() {
    return {
      companies: this.clients?.map(client => client.company) || [],
      companiesSearch: [],
      query: '',
    };
  },
  watch: {
    companies() {
      this.emitChanges();
    },
  },
  methods: {
    handleQueryChange(query: string): void {
      this.query = query;
    },
    async handleCompanyCreate(name: string): Promise<void> {
      // @ts-ignore createClient comes from mixin
      const company = await this.createCompany(name);

      this.addCompany(company);
    },
    handleCompanyChange(companies: AutocompleteCompany[]) {
      this.companies = companies;
    },
    addCompany(company: Company): void {
      this.companies.push(company);

      this.emitChanges();
    },
    emitChanges() {
      this.$emit('change', { attributeName: this.attributeName, companies: this.companies });
    },
  },
});
